/**
 * Hard-delete confirmation, lifted out of the tenants list when per-tenant
 * actions moved to the detail page.
 *
 * Two-step UX: opens on plan (calls dryRun=true), shows the cascade plan +
 * any vetoes that need bypass flags. The operator must type the tenant id
 * back, tick all required bypasses, then click "Delete permanently" — which
 * calls dryRun=false. Dry-run is auto-fired when the modal opens; real run
 * is gated on the typed id matching exactly.
 */
import { useEffect, useState } from 'react';
import { Modal, Button, FormField, Input } from '@shared-ui/components';
import {
    tenantsApi,
    type Tenant,
    type HardDeleteRequest,
    type HardDeleteResponse,
} from '../../../api/tenantsApi';
import './Tenants.css';

export function HardDeleteTenantModal(props: {
    tenant: Tenant | null;
    onClose: () => void;
    onDeleted: () => void;
}) {
    const [plan, setPlan] = useState<HardDeleteResponse | null>(null);
    const [planError, setPlanError] = useState('');
    const [planning, setPlanning] = useState(false);

    const [allowProd, setAllowProd] = useState(false);
    const [allowUsers, setAllowUsers] = useState(false);
    const [allowActivity, setAllowActivity] = useState(false);
    const [allowLarge, setAllowLarge] = useState(false);

    const [confirmId, setConfirmId] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [result, setResult] = useState<HardDeleteResponse | null>(null);

    // Reset + auto-plan whenever a new tenant is opened.
    useEffect(() => {
        if (!props.tenant) return;
        setPlan(null);
        setPlanError('');
        setAllowProd(false);
        setAllowUsers(false);
        setAllowActivity(false);
        setAllowLarge(false);
        setConfirmId('');
        setResult(null);
        setDeleteError('');
        setPlanning(true);
        const tenantId = props.tenant.id;
        tenantsApi.hardDelete(tenantId, { confirmTenantId: tenantId }, { dryRun: true })
            .then(setPlan)
            .catch((err: Error) => setPlanError(err.message))
            .finally(() => setPlanning(false));
    }, [props.tenant]);

    if (!props.tenant) {
        return <Modal isOpen={false} onClose={props.onClose}>{null}</Modal>;
    }

    const tenant = props.tenant;
    const idMatch = confirmId === tenant.id;

    const bypassesNeeded = plan
        ? {
            allowProd: plan.vetoes.prodEndpoint,
            allowUsers: plan.vetoes.registeredUsers,
            allowActivity: plan.vetoes.hasActivity,
            allowLarge: plan.vetoes.largeCascade,
        }
        : { allowProd: false, allowUsers: false, allowActivity: false, allowLarge: false };

    const bypassesSatisfied =
        (!bypassesNeeded.allowProd || allowProd)
        && (!bypassesNeeded.allowUsers || allowUsers)
        && (!bypassesNeeded.allowActivity || allowActivity)
        && (!bypassesNeeded.allowLarge || allowLarge);

    const canDelete = !!plan && idMatch && bypassesSatisfied && !deleting && !result;

    async function handleDelete() {
        if (!canDelete) return;
        setDeleting(true);
        setDeleteError('');
        try {
            const body: HardDeleteRequest = {
                confirmTenantId: tenant.id,
                ...(allowProd ? { allowProd: true } : {}),
                ...(allowUsers ? { allowUsers: true } : {}),
                ...(allowActivity ? { allowActivity: true } : {}),
                ...(allowLarge ? { allowLarge: true } : {}),
            };
            const resp = await tenantsApi.hardDelete(tenant.id, body, { dryRun: false });
            setResult(resp);
        } catch (err) {
            setDeleteError(err instanceof Error ? err.message : String(err));
        } finally {
            setDeleting(false);
        }
    }

    return (
        <Modal
            isOpen={true}
            onClose={() => { if (!deleting) props.onClose(); }}
            title={`Hard delete tenant: ${tenant.id}`}
            size="md"
            footer={
                result ? (
                    <Button onClick={() => { props.onDeleted(); }}>Done</Button>
                ) : (
                    <>
                        <Button variant="outline" onClick={props.onClose} disabled={deleting}>Cancel</Button>
                        <Button
                            variant="danger"
                            onClick={handleDelete}
                            disabled={!canDelete}
                            loading={deleting}
                        >
                            Delete permanently
                        </Button>
                    </>
                )
            }
        >
            <div className="hard-delete-modal">
                {!result && (
                    <div className="hard-delete-warning">
                        <strong>This action is irreversible.</strong>
                        Hard delete removes the tenant doc, every user belonging to it, and
                        all of those users' submissions, scores, sessions, and other
                        user-scoped data. Audit-logs are kept for forensics.
                    </div>
                )}

                {planning && <div>Computing cascade plan…</div>}
                {planError && <div className="tenants-error">Plan failed: {planError}</div>}

                {plan && !result && (
                    <>
                        <CascadeCountsTable plan={plan} />

                        {anyBypassNeeded(bypassesNeeded) && (
                            <div className="hard-delete-bypasses">
                                <strong>Required overrides</strong>
                                {bypassesNeeded.allowProd && (
                                    <label className="veto-required">
                                        <input
                                            type="checkbox"
                                            checked={allowProd}
                                            onChange={e => setAllowProd(e.target.checked)}
                                        />
                                        <span>I understand this is the <strong>prod</strong> Cosmos.</span>
                                    </label>
                                )}
                                {bypassesNeeded.allowUsers && (
                                    <label className="veto-required">
                                        <input
                                            type="checkbox"
                                            checked={allowUsers}
                                            onChange={e => setAllowUsers(e.target.checked)}
                                        />
                                        <span>
                                            This tenant has {plan.plan.registeredUserCount - plan.plan.testUserCount} non-test
                                            registered user(s). I want them deleted.
                                        </span>
                                    </label>
                                )}
                                {bypassesNeeded.allowActivity && (
                                    <label className="veto-required">
                                        <input
                                            type="checkbox"
                                            checked={allowActivity}
                                            onChange={e => setAllowActivity(e.target.checked)}
                                        />
                                        <span>
                                            This tenant has activity rows. I want them all removed.
                                        </span>
                                    </label>
                                )}
                                {bypassesNeeded.allowLarge && (
                                    <label className="veto-required">
                                        <input
                                            type="checkbox"
                                            checked={allowLarge}
                                            onChange={e => setAllowLarge(e.target.checked)}
                                        />
                                        <span>
                                            Cascade size ({plan.plan.totalCount.toLocaleString()} rows)
                                            exceeds the safety threshold. Proceed anyway.
                                        </span>
                                    </label>
                                )}
                            </div>
                        )}

                        <FormField label={`Type "${tenant.id}" to confirm`} required>
                            <Input
                                type="text"
                                value={confirmId}
                                onChange={e => setConfirmId(e.target.value)}
                                placeholder={tenant.id}
                                className="hard-delete-confirm-input"
                            />
                        </FormField>

                        {deleteError && <div className="tenants-error">Delete failed: {deleteError}</div>}
                    </>
                )}

                {result && result.result && (
                    <div className="hard-delete-result">
                        <strong>Tenant "{tenant.id}" hard-deleted.</strong>
                        <div>Deleted {result.result.totalDeleted.toLocaleString()} row(s) total
                            (users: {result.result.usersDeleted}, tenant: {result.result.tenantDeleted ? 'yes' : 'no'}).
                        </div>
                        {result.result.errors.length > 0 && (
                            <div>
                                {result.result.errors.length} per-row error(s) — see audit log <code>{result.auditLogId}</code>.
                            </div>
                        )}
                        {result.result.errors.length === 0 && (
                            <div>Audit log id: <code>{result.auditLogId}</code></div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
}

function anyBypassNeeded(b: { allowProd: boolean; allowUsers: boolean; allowActivity: boolean; allowLarge: boolean }): boolean {
    return b.allowProd || b.allowUsers || b.allowActivity || b.allowLarge;
}

function CascadeCountsTable({ plan }: { plan: HardDeleteResponse }) {
    const entries = Object.entries(plan.plan.perContainerCounts);
    return (
        <div className="hard-delete-counts">
            <table>
                <thead>
                    <tr>
                        <th>Container</th>
                        <th style={{ textAlign: 'right' }}>Rows to delete</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            users (in this tenant)
                            {plan.plan.testUserCount > 0 && (
                                <small style={{ display: 'block', color: 'var(--text-tertiary)' }}>
                                    {plan.plan.testUserCount} test user(s) excluded from vetoes
                                </small>
                            )}
                        </td>
                        <td className="count-cell">{plan.plan.userIds.length}</td>
                    </tr>
                    {entries.map(([container, count]) => (
                        <tr key={container} className={count === 0 ? 'zero-row' : ''}>
                            <td>{container}</td>
                            <td className="count-cell">{count.toLocaleString()}</td>
                        </tr>
                    ))}
                    <tr>
                        <td>tenants (this tenant doc)</td>
                        <td className="count-cell">1</td>
                    </tr>
                    <tr className="total-row">
                        <td>Total</td>
                        <td className="count-cell">{plan.plan.totalCount.toLocaleString()}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
